/**
 * Staff side of the client lifecycle: proposals with their engagement letter, recurring fees and
 * automatic payment, offboarding, and the settings for proposals and review requests.
 */
import {
  DEFAULT_LIFECYCLE_SETTINGS,
  INTERVAL_LABELS,
  MODULES,
  MODULE_LABELS,
  OFFBOARDING_REASONS,
  OFFBOARDING_REASON_LABELS,
  PROPOSAL_BILLING,
  PROPOSAL_BILLING_LABELS,
  formatCents,
  lifecycleSettings,
  parseDollars,
  proposalTotals,
  type LifecycleSettings,
  type Module,
  type ProposalBillingKind,
  type ScheduleInterval,
} from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileText, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { ask } from "@/components/dialogs";
import { Empty, ListSearch, Loading, LoadingBox, PageHeader, Pager, Section, StatRow, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate, localToday, plural } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import type { Client } from "@/lib/types";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

export type ProposalStatus = "draft" | "sent" | "accepted" | "declined" | "withdrawn" | "expired";
const STATUS: Record<ProposalStatus, { label: string; tone: string }> = {
  draft: { label: "Draft", tone: "bg-secondary text-foreground" },
  sent: { label: "Waiting for client", tone: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200" },
  accepted: { label: "Accepted", tone: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" },
  declined: { label: "Declined", tone: "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200" },
  withdrawn: { label: "Withdrawn", tone: "bg-secondary text-muted-foreground" },
  expired: { label: "Expired", tone: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200" },
};

export function ProposalBadge({ status }: { status: ProposalStatus }) {
  const s = STATUS[status];
  return <span className={cn("whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium", s.tone)}>{s.label}</span>;
}

/** "$450.00 a month" */
export function feeText(amountCents: number, billing: ProposalBillingKind | ScheduleInterval): string {
  const per: Record<string, string> = { monthly: "a month", month: "a month", quarterly: "a quarter", quarter: "a quarter", annual: "a year", year: "a year", one_time: "once" };
  return `${formatCents(amountCents)} ${per[billing]}`;
}

type ProposalRow = {
  id: string;
  number: number;
  kind: "new" | "renewal";
  status: ProposalStatus;
  title: string;
  clientId: string;
  clientName: string;
  startsOn: string;
  sentAt: string | null;
  validUntil: string | null;
  acceptedAt: string | null;
  createdAt: string;
  oneTimeCents: number;
  annualCents: number;
};
type ProposalPage = { items: ProposalRow[]; total: number; counts: Partial<Record<ProposalStatus, number>>; renewalsToReview: number };

// --- Billing › Proposals -------------------------------------------------------------------

export function ProposalsTab({ me }: { me: Me }) {
  const [status, setStatus] = useState<"" | ProposalStatus>("");
  const [kind, setKind] = useState<"" | "renewal">("");
  const [q, setQ] = useState("");
  const { offset, size, paged } = useServerPaged(`${status}|${kind}|${q.trim()}`);
  const params = new URLSearchParams({ offset: String(offset), limit: String(size) });
  if (status) params.set("status", status);
  if (kind) params.set("kind", kind);
  if (q.trim()) params.set("q", q.trim());
  const { data, isLoading } = useQuery<ProposalPage>({ queryKey: [`/api/proposals?${params}`], placeholderData: (p) => p });
  const c = data?.counts ?? {};
  const toggle = (s: ProposalStatus | "renewal") => {
    if (s === "renewal") {
      setKind(kind === "renewal" ? "" : "renewal");
      setStatus(kind === "renewal" ? "" : "draft");
    } else {
      setKind("");
      setStatus(status === s ? "" : s);
    }
  };
  return (
    <>
      <PageHeader
        title="Proposals"
        description="Proposals with their engagement letter, priced for each client. The client accepts and signs in the portal."
        actions={hasPermission(me, "billing.write") && <NewProposalButton />}
      />
      <StatRow
        stats={[
          { label: "Drafts", value: c.draft ?? 0, selected: status === "draft" && !kind, onClick: () => toggle("draft") },
          { label: "Renewals to review", value: data?.renewalsToReview ?? 0, tone: data?.renewalsToReview ? "warning" : "default", selected: kind === "renewal", onClick: () => toggle("renewal") },
          { label: "Waiting for clients", value: c.sent ?? 0, selected: status === "sent", onClick: () => toggle("sent") },
          { label: "Accepted", value: c.accepted ?? 0, tone: "success", selected: status === "accepted", onClick: () => toggle("accepted") },
        ]}
      />
      <ListSearch value={q} onChange={setQ} placeholder="Search by client, title or number" className="mb-4" />
      {isLoading ? (
        <Loading />
      ) : !data?.items.length ? (
        <Empty>{q.trim() || status || kind ? "No proposals match." : "No proposals yet. Choose New proposal to price services for a client and send them the engagement letter."}</Empty>
      ) : (
        <TableScroll label="Proposals table" className="rounded-lg border border-card-border bg-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Proposal</th>
                <th className="px-3 py-2 font-medium">Client</th>
                <th className="px-3 py-2 text-right font-medium">One-time</th>
                <th className="px-3 py-2 text-right font-medium">Recurring a year</th>
                <th className="px-3 py-2 font-medium">Starts</th>
                <th className="px-4 py-2 text-right font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.items.map((p) => (
                <tr key={p.id} className="hover:bg-muted/40">
                  <td className="max-w-[18rem] px-4 py-2.5">
                    <Link href={`/billing/proposals/${p.id}`} className="block truncate font-medium text-primary hover:underline">
                      {p.title}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      No. {p.number}
                      {p.kind === "renewal" && " · Renewal"}
                    </span>
                  </td>
                  <td className="max-w-[14rem] truncate px-3 py-2.5">{p.clientName}</td>
                  <td className="tabular px-3 py-2.5 text-right">{p.oneTimeCents ? formatCents(p.oneTimeCents) : "—"}</td>
                  <td className="tabular px-3 py-2.5 text-right">{p.annualCents ? formatCents(p.annualCents) : "—"}</td>
                  <td className="tabular whitespace-nowrap px-3 py-2.5">{formatDate(p.startsOn)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <ProposalBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager paged={paged(data.total)} noun="proposals" className="px-4 pb-3" />
        </TableScroll>
      )}
    </>
  );
}

function NewProposalButton() {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data } = useQuery<{ items: Client[] }>({ queryKey: [`/api/clients?limit=20${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`], enabled: open });
  const clients = (data?.items ?? []).filter((c) => c.status !== "archived");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" /> New proposal
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New proposal</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="prop-client">Client or prospect</Label>
          <Input id="prop-client" autoFocus placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} />
          <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
            {clients.map((c) => (
              <li key={c.id}>
                <button className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60" onClick={() => navigate(`/billing/proposals/new?clientId=${c.id}`)}>
                  <span className="min-w-0 truncate">{c.displayName}</span>
                  {c.status === "prospect" && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium">Prospect</span>}
                </button>
              </li>
            ))}
            {data && !clients.length && <li className="px-3 py-2 text-sm text-muted-foreground">No clients match. Add the client or convert the lead first.</li>}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- The proposal: edit a draft, or view a sent one ------------------------------------------

type Item = { module: Module; description: string; billing: ProposalBillingKind; amount: string; templateId: string | null; period: string };
type Detail = {
  proposal: {
    id: string;
    number: number;
    kind: "new" | "renewal";
    status: ProposalStatus;
    title: string;
    message: string;
    terms: string;
    startsOn: string;
    endsOn: string;
    termMonths: number;
    validUntil: string | null;
    sentAt: string | null;
    viewedAt: string | null;
    acceptedAt: string | null;
    acceptedName: string | null;
    declinedAt: string | null;
    declineNote: string | null;
    letterDocumentId: string | null;
    signedDocumentId: string | null;
    renewalOf: string | null;
    clientId: string;
    totals: { oneTimeCents: number; annualCents: number; firstYearCents: number };
  };
  items: Array<{ id: string; module: Module; description: string; billing: ProposalBillingKind; amountCents: number; templateId: string | null; period: string | null; scheduleId: string | null }>;
  client: { id: string; displayName: string; status: string } | null;
  renewal: { id: string; number: number; status: ProposalStatus } | null;
};
type Template = { id: string; module: Module; name: string; kind: string; isActive?: boolean };

const blankItem = (): Item => ({ module: "tax", description: "", billing: "one_time", amount: "", templateId: null, period: "" });

const cents = (s: string): number | null => {
  try {
    const v = parseDollars(s || "0");
    return v >= 0 ? v : null;
  } catch {
    return null;
  }
};

export function ProposalPage({ id, me, clientId }: { id: string | null; me: Me; clientId?: string | null }) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<Detail>({ queryKey: [`/api/proposals/${id}`], enabled: !!id });
  const { data: settings } = useQuery<Record<string, unknown>>({ queryKey: ["/api/firm/settings"] });
  const { data: templates } = useQuery<Template[]>({ queryKey: ["/api/templates"] });
  const { data: client } = useQuery<{ client: { displayName: string } }>({ queryKey: [`/api/clients/${clientId}`], enabled: !id && !!clientId });
  const cfg = lifecycleSettings(settings?.clientLifecycle);
  const canWrite = hasPermission(me, "billing.write");

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [terms, setTerms] = useState("");
  const [startsOn, setStartsOn] = useState(localToday());
  const [termMonths, setTermMonths] = useState("12");
  const [items, setItems] = useState<Item[]>([blankItem()]);
  const [showTerms, setShowTerms] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    if (id && data) {
      const p = data.proposal;
      setTitle(p.title);
      setMessage(p.message);
      setTerms(p.terms);
      setStartsOn(p.startsOn);
      setTermMonths(String(p.termMonths));
      setItems(data.items.map((i) => ({ module: i.module, description: i.description, billing: i.billing, amount: (i.amountCents / 100).toFixed(2), templateId: i.templateId, period: i.period ?? "" })));
      setLoaded(true);
    } else if (!id && settings) {
      setTerms(cfg.terms);
      setLoaded(true);
    }
  }, [id, data, settings, loaded, cfg.terms]);

  const priced = items.map((i) => ({ ...i, cents: cents(i.amount) }));
  const invalid = !title.trim() || priced.some((i) => !i.description.trim() || i.cents === null) || !items.length;
  const totals = proposalTotals(priced.map((i) => ({ billing: i.billing, amountCents: i.cents ?? 0 })));
  const body = () => ({
    title: title.trim(),
    message: message.trim(),
    terms: terms.trim() || undefined,
    startsOn,
    termMonths: Number(termMonths),
    items: priced.map((i) => ({ module: i.module, description: i.description.trim(), billing: i.billing, amountCents: i.cents!, templateId: i.billing === "one_time" ? i.templateId : null, period: i.period.trim() || null })),
  });

  const save = useMutation({
    mutationFn: () => (id ? api.send<Detail>("PUT", `/api/proposals/${id}`, body()) : api.send<Detail>("POST", "/api/proposals", { clientId, ...body() })),
    onSuccess: (d) => {
      toast({ title: "Draft saved" });
      if (!id) navigate(`/billing/proposals/${d.proposal.id}`, { replace: true });
    },
  });
  const send = useMutation({
    mutationFn: async () => {
      await api.send("PUT", `/api/proposals/${id}`, body());
      return api.send<Detail>("POST", `/api/proposals/${id}/send`);
    },
    onSuccess: () => toast({ title: "Proposal sent", description: "The client has been emailed a link to review and accept it in the portal." }),
  });
  const act = useMutation({ mutationFn: (path: string) => api.send<Detail>("POST", `/api/proposals/${id}/${path}`), onSuccess: (d) => d?.proposal?.id && d.proposal.id !== id && navigate(`/billing/proposals/${d.proposal.id}`) });
  const del = useMutation({ mutationFn: () => api.send("DELETE", `/api/proposals/${id}`), onSuccess: () => navigate("/billing/proposals") });

  if (id && isLoading) return <Loading />;
  if (id && (error || !data)) return <Empty>{errorMessage(error)}</Empty>;
  const p = data?.proposal;
  const editable = canWrite && (!p || p.status === "draft");
  const clientName = data?.client?.displayName ?? client?.client.displayName ?? "";
  const set = (n: number, patch: Partial<Item>) => setItems((xs) => xs.map((x, i) => (i === n ? { ...x, ...patch } : x)));

  return (
    <>
      <PageHeader
        crumbs={[{ label: "Billing", href: "/billing" }, { label: "Proposals", href: "/billing/proposals" }, { label: p ? `No. ${p.number}` : "New" }]}
        title={p ? p.title : "New proposal"}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {clientName && (
              <Link href={`/clients/${p?.clientId ?? clientId}?tab=billing`} className="font-medium text-primary hover:underline">
                {clientName}
              </Link>
            )}
            {p && <ProposalBadge status={p.status} />}
            {p?.kind === "renewal" && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium">Renewal</span>}
          </span>
        }
        actions={
          <>
            {editable && p && (
              <Button variant="ghost" onClick={async () => (await ask({ title: "Delete this draft?", confirmLabel: "Delete draft", destructive: true })) && del.mutate()}>
                Delete draft
              </Button>
            )}
            {editable && (
              <Button variant="outline" disabled={invalid || save.isPending} onClick={() => save.mutate()}>
                Save draft
              </Button>
            )}
            {editable && p && (
              <Button
                disabled={invalid || send.isPending}
                onClick={async () =>
                  (await ask({ title: `Send to ${clientName}?`, description: "The engagement letter is made from this proposal as it stands and can't be changed after it is sent. The client is emailed a link to review and accept it in the portal.", confirmLabel: "Send proposal" })) && send.mutate()
                }
              >
                Send to client
              </Button>
            )}
            {canWrite && p?.status === "sent" && (
              <Button variant="outline" onClick={async () => (await ask({ title: "Withdraw this proposal?", description: "The client will no longer be able to accept it.", confirmLabel: "Withdraw", destructive: true })) && act.mutate("withdraw")}>
                Withdraw
              </Button>
            )}
            {canWrite && p && p.status !== "draft" && (
              <Button variant="outline" onClick={() => act.mutate("copy")}>
                Copy to a new draft
              </Button>
            )}
          </>
        }
      />
      {(save.error || send.error) && <p className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{errorMessage(save.error ?? send.error)}</p>}
      {p && p.status !== "draft" && <ProposalTimeline d={data!} />}

      <div className="space-y-6">
        <Section title="Proposal" description="What the client sees first: a title, the start of the term and a short note.">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_10rem_8rem]">
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="p-title">Title</Label>
              <Input id="p-title" value={title} disabled={!editable} onChange={(e) => setTitle(e.target.value)} placeholder="Bookkeeping, payroll and the 2026 return" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-start">Starts on</Label>
              <Input id="p-start" type="date" value={startsOn} disabled={!editable} onChange={(e) => setStartsOn(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-term">Term (months)</Label>
              <Input id="p-term" type="number" min={1} max={36} value={termMonths} disabled={!editable} onChange={(e) => setTermMonths(e.target.value)} />
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            <Label htmlFor="p-msg">Note to the client</Label>
            <Textarea id="p-msg" rows={3} value={message} disabled={!editable} onChange={(e) => setMessage(e.target.value)} placeholder="Thank you for meeting with us. This proposal covers…" />
          </div>
        </Section>

        <Section title="Services and fees" description="Each service at the price agreed with this client. One-time work can open its workflow when the client accepts; recurring fees are invoiced on their schedule.">
          <div className="space-y-3">
            {items.map((it, n) => (
              <div key={n} className="grid gap-3 rounded-lg border border-border p-3 md:grid-cols-[11.5rem_minmax(0,1fr)_8rem_8rem_2.25rem]">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Service</Label>
                  <select aria-label="Service" className={select} value={it.module} disabled={!editable} onChange={(e) => set(n, { module: e.target.value as Module, templateId: null })}>
                    {MODULES.map((m) => (
                      <option key={m} value={m}>
                        {MODULE_LABELS[m]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0 space-y-1">
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Input aria-label="Description" value={it.description} disabled={!editable} onChange={(e) => set(n, { description: e.target.value })} placeholder="Monthly bookkeeping and close" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Billed</Label>
                  <select aria-label="Billed" className={select} value={it.billing} disabled={!editable} onChange={(e) => set(n, { billing: e.target.value as ProposalBillingKind, templateId: e.target.value === "one_time" ? it.templateId : null })}>
                    {PROPOSAL_BILLING.map((b) => (
                      <option key={b} value={b}>
                        {PROPOSAL_BILLING_LABELS[b]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fee ($)</Label>
                  <Input aria-label="Fee" inputMode="decimal" className={cn("tabular text-right", it.amount && cents(it.amount) === null && "border-destructive")} value={it.amount} disabled={!editable} onChange={(e) => set(n, { amount: e.target.value })} placeholder="0.00" />
                </div>
                <div className="flex items-end">
                  {editable && items.length > 1 && (
                    <Button variant="ghost" size="icon" aria-label="Remove this service" onClick={() => setItems((xs) => xs.filter((_, i) => i !== n))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {it.billing === "one_time" && (
                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem] md:col-span-4 md:col-start-2">
                    <div className="min-w-0 space-y-1">
                      <Label className="text-xs text-muted-foreground">Open this workflow when accepted (optional)</Label>
                      <select aria-label="Workflow" className={select} value={it.templateId ?? ""} disabled={!editable} onChange={(e) => set(n, { templateId: e.target.value || null })}>
                        <option value="">None: a draft invoice is created instead</option>
                        {(templates ?? [])
                          .filter((t) => t.module === it.module)
                          .map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Year or period</Label>
                      <Input aria-label="Year or period" value={it.period} disabled={!editable} onChange={(e) => set(n, { period: e.target.value })} placeholder="2026" />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {editable && (
              <Button variant="outline" size="sm" onClick={() => setItems((xs) => [...xs, blankItem()])}>
                <Plus className="mr-1 h-4 w-4" /> Add a service
              </Button>
            )}
          </div>
          <dl className="mt-5 grid gap-3 border-t border-border pt-4 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">One-time fees</dt>
              <dd className="tabular text-lg font-semibold">{formatCents(totals.oneTimeCents)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Recurring fees for a year</dt>
              <dd className="tabular text-lg font-semibold">{formatCents(totals.annualCents)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">First year in total</dt>
              <dd className="tabular text-lg font-semibold">{formatCents(totals.firstYearCents)}</dd>
            </div>
          </dl>
        </Section>

        <Section
          title="Engagement letter terms"
          description="Scope, responsibilities, fees, term and renewal, and how the engagement ends. {{firm}} and {{client}} are filled in when the letter is made."
          action={
            <Button variant="ghost" size="sm" onClick={() => setShowTerms((x) => !x)}>
              {showTerms ? "Hide terms" : "Show terms"}
            </Button>
          }
        >
          {showTerms ? (
            <Textarea aria-label="Engagement letter terms" rows={18} className="font-mono text-xs" value={terms} disabled={!editable} onChange={(e) => setTerms(e.target.value)} />
          ) : (
            <p className="text-sm text-muted-foreground">
              {terms === cfg.terms ? "The firm's standard terms (Firm Settings › Services and workflows › Proposals and billing)." : "Terms changed for this proposal."} {plural(terms.split(/\n\s*\n/).length, "paragraph")}.
            </p>
          )}
        </Section>
      </div>
    </>
  );
}

function ProposalTimeline({ d }: { d: Detail }) {
  const p = d.proposal;
  const doc = (docId: string | null, label: string) =>
    docId && (
      <a href={`/api/documents/${docId}/download`} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
        <FileText className="h-4 w-4" aria-hidden /> {label}
      </a>
    );
  const facts: Array<[string, string]> = [
    ["Sent", p.sentAt ? formatDate(p.sentAt) : "—"],
    ["Opened by the client", p.viewedAt ? formatDate(p.viewedAt) : "Not yet"],
    [p.status === "accepted" ? "Accepted" : p.status === "declined" ? "Declined" : "Can be accepted until", p.acceptedAt ? formatDate(p.acceptedAt) : p.declinedAt ? formatDate(p.declinedAt) : p.validUntil ? formatDate(p.validUntil) : "—"],
    ["Term", `${formatDate(p.startsOn)} to ${formatDate(p.endsOn)}`],
  ];
  return (
    <div className="mb-6 rounded-lg border border-card-border bg-card p-4">
      <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {facts.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-xs text-muted-foreground">{k}</dt>
            <dd className="tabular font-medium">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 border-t border-border pt-3">
        {doc(p.letterDocumentId, "Engagement letter as sent")}
        {doc(p.signedDocumentId, `Signed by ${p.acceptedName ?? "the client"}, with certificate`)}
        {p.declineNote && <span className="text-sm text-muted-foreground">Client's note: {p.declineNote}</span>}
        {d.renewal && (
          <Link href={`/billing/proposals/${d.renewal.id}`} className="text-sm font-medium text-primary hover:underline">
            Renewal No. {d.renewal.number} ({STATUS[d.renewal.status].label.toLowerCase()})
          </Link>
        )}
      </div>
    </div>
  );
}

// --- Billing › Recurring ---------------------------------------------------------------------

export type Schedule = {
  id: string;
  clientId: string;
  clientName?: string;
  module: Module;
  description: string;
  amountCents: number;
  pendingAmountCents: number | null;
  pendingFrom: string | null;
  interval: ScheduleInterval;
  nextOn: string;
  endsOn: string | null;
  status: "active" | "paused" | "ended";
  autopay?: string | null;
};

function ScheduleStatus({ s }: { s: Schedule }) {
  if (s.status === "paused") return <span className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">Paused</span>;
  if (s.status === "ended") return <span className="whitespace-nowrap rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">Ended</span>;
  return s.autopay ? (
    <span className="whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">Autopay on</span>
  ) : (
    <span className="whitespace-nowrap rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-900 dark:bg-blue-900/30 dark:text-blue-200">Invoiced</span>
  );
}

function ScheduleActions({ s, me }: { s: Schedule; me: Me }) {
  const patch = useMutation({ mutationFn: (b: object) => api.send("PATCH", `/api/billing-schedules/${s.id}`, b) });
  const end = useMutation({ mutationFn: () => api.send("POST", `/api/billing-schedules/${s.id}/end`) });
  if (s.status === "ended" || !hasPermission(me, "billing.write")) return null;
  return (
    <span className="inline-flex gap-1">
      <Button variant="ghost" size="sm" onClick={() => patch.mutate({ status: s.status === "paused" ? "active" : "paused" })}>
        {s.status === "paused" ? "Resume" : "Pause"}
      </Button>
      {hasPermission(me, "billing.manage") && (
        <Button variant="ghost" size="sm" onClick={async () => (await ask({ title: "End this recurring fee?", description: "No further invoices will be issued for it. Invoices already issued are not changed.", confirmLabel: "End fee", destructive: true })) && end.mutate()}>
          End
        </Button>
      )}
    </span>
  );
}

export function ScheduleTable({ rows, me, showClient }: { rows: Schedule[]; me: Me; showClient?: boolean }) {
  return (
    <TableScroll label="Recurring fees table" className="rounded-lg border border-card-border bg-card">
      <table className={cn("w-full text-sm", showClient ? "min-w-[820px]" : "min-w-[640px]")}>
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            {showClient && <th className="px-4 py-2 font-medium">Client</th>}
            <th className={cn("py-2 font-medium", showClient ? "px-3" : "px-4")}>Fee</th>
            <th className="px-3 py-2 text-right font-medium">Amount</th>
            <th className="px-3 py-2 font-medium">Next invoice</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-4 py-2"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((s) => (
            <tr key={s.id}>
              {showClient && (
                <td className="max-w-[12rem] truncate px-4 py-2.5">
                  <Link href={`/clients/${s.clientId}?tab=billing`} className="text-primary hover:underline">
                    {s.clientName}
                  </Link>
                </td>
              )}
              <td className={cn("max-w-[16rem] py-2.5", showClient ? "px-3" : "px-4")}>
                <span className="block truncate">{s.description}</span>
                <span className="text-xs text-muted-foreground">{MODULE_LABELS[s.module]}</span>
              </td>
              <td className="tabular whitespace-nowrap px-3 py-2.5 text-right">
                {feeText(s.amountCents, s.interval)}
                {s.pendingAmountCents && s.pendingFrom && <span className="block text-xs text-muted-foreground">{feeText(s.pendingAmountCents, s.interval)} from {formatDate(s.pendingFrom)}</span>}
              </td>
              <td className="tabular whitespace-nowrap px-3 py-2.5">{s.status === "ended" ? "—" : formatDate(s.nextOn)}</td>
              <td className="px-3 py-2.5">
                <ScheduleStatus s={s} />
              </td>
              <td className="px-4 py-2.5 text-right">
                <ScheduleActions s={s} me={me} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

export function RecurringTab({ me }: { me: Me }) {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery<Schedule[]>({ queryKey: ["/api/billing-schedules"] });
  const rows = (data ?? []).filter((s) => !q.trim() || `${s.clientName} ${s.description}`.toLowerCase().includes(q.trim().toLowerCase()));
  const { offset, size, paged } = useServerPaged(q);
  const active = (data ?? []).filter((s) => s.status === "active");
  const monthly = active.reduce((a, s) => a + Math.round((s.pendingAmountCents ?? s.amountCents) / (s.interval === "month" ? 1 : s.interval === "quarter" ? 3 : 12)), 0);
  const today = localToday();
  const week = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  return (
    <>
      <PageHeader title="Recurring fees" description="Each recurring fee is invoiced on its date. Clients pay by any method, or automatically from a card or bank account they saved in the portal." />
      <StatRow
        stats={[
          { label: "Active recurring fees", value: active.length },
          { label: "Recurring revenue a month", value: formatCents(monthly) },
          { label: "Invoiced in the next 7 days", value: active.filter((s) => s.nextOn >= today && s.nextOn <= week).length },
          { label: "On automatic payment", value: active.filter((s) => s.autopay).length, tone: "success" },
        ]}
      />
      <ListSearch value={q} onChange={setQ} placeholder="Search by client or fee" className="mb-4" />
      {isLoading ? (
        <Loading />
      ) : !rows.length ? (
        <Empty>{q.trim() ? "No recurring fees match." : "No recurring fees yet. They are set up when a client accepts a proposal, or added on the client's Billing tab."}</Empty>
      ) : (
        <>
          <ScheduleTable rows={rows.slice(offset, offset + size)} me={me} showClient />
          <Pager paged={paged(rows.length)} noun="recurring fees" className="mt-2" />
        </>
      )}
    </>
  );
}

// --- The client's Billing tab ----------------------------------------------------------------

type Profile = {
  methods: Array<{ id: string; kind: string; label: string; autopay: boolean; autopayConsentAt: string | null }>;
  schedules: Schedule[];
  proposals: Array<{ id: string; number: number; kind: "new" | "renewal"; status: ProposalStatus; title: string; startsOn: string; endsOn: string }>;
  autopayAvailable: boolean;
};

export function ClientBillingProfile({ clientId, me, onboarding }: { clientId: string; me: Me; onboarding?: boolean }) {
  const { data } = useQuery<Profile>({ queryKey: [`/api/clients/${clientId}/billing-profile`] });
  const [adding, setAdding] = useState(false);
  if (!data) return <Loading size="list" />;
  const autopay = data.methods.find((m) => m.autopay);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Section
        title="Proposals"
        description="Services and fees offered to this client, with the engagement letter."
        action={
          hasPermission(me, "billing.write") && (
            <Button size="sm" variant="outline" asChild>
              <Link href={`/billing/proposals/new?clientId=${clientId}`}>
                <Plus className="mr-1 h-4 w-4" /> New proposal
              </Link>
            </Button>
          )
        }
      >
        {!data.proposals.length ? (
          <Empty>{onboarding ? "No proposal yet. Price the services for this client and send the engagement letter with New proposal." : "No proposals for this client."}</Empty>
        ) : (
          <ul className="divide-y divide-border">
            {data.proposals.slice(0, 5).map((p) => (
              <li key={p.id} className="flex items-center gap-3 py-2 text-sm">
                <Link href={`/billing/proposals/${p.id}`} className="min-w-0 flex-1 truncate text-primary hover:underline">
                  {p.title}
                </Link>
                <span className="tabular hidden whitespace-nowrap text-xs text-muted-foreground sm:inline">{formatDate(p.startsOn)}</span>
                <ProposalBadge status={p.status} />
              </li>
            ))}
          </ul>
        )}
        {data.proposals.length > 5 && (
          <Link href={`/billing/proposals`} className="mt-2 inline-block text-sm text-primary hover:underline">
            All proposals
          </Link>
        )}
      </Section>
      <Section title="Payment method" description="Saved by the client in the portal. Only Stripe holds the card or bank details.">
        {!data.methods.length ? (
          <Empty>{data.autopayAvailable ? "No payment method saved. The client can save a card or bank account, and turn on automatic payment, under Invoices & Payments in the portal." : "Online payment is not set up for this firm."}</Empty>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {data.methods.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate font-medium">{m.label}</span>
                  <span className="text-xs text-muted-foreground">{m.autopay ? `Automatic payment authorized ${formatDate(m.autopayConsentAt)}` : "Saved, not used for automatic payment"}</span>
                </span>
                {m.autopay && <span className="whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">Autopay on</span>}
              </li>
            ))}
          </ul>
        )}
        {!autopay && data.methods.length > 0 && <p className="mt-2 text-xs text-muted-foreground">Automatic payment is off: recurring invoices are emailed for the client to pay.</p>}
      </Section>
      <Section
        className="lg:col-span-2"
        title="Recurring fees"
        description="Invoiced on their date. A renewal's new price starts with the new term."
        action={
          hasPermission(me, "billing.write") &&
          !adding && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="mr-1 h-4 w-4" /> Add a recurring fee
            </Button>
          )
        }
      >
        {adding && <NewSchedule clientId={clientId} onDone={() => setAdding(false)} />}
        {!data.schedules.length ? <Empty>No recurring fees. They are set up when the client accepts a proposal, or added here.</Empty> : <ScheduleTable rows={data.schedules} me={me} />}
      </Section>
    </div>
  );
}

function NewSchedule({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const [module, setModule] = useState<Module>("bookkeeping");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState<ScheduleInterval>("month");
  const [nextOn, setNextOn] = useState(localToday());
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/billing-schedules", { clientId, module, description: description.trim(), amountCents: cents(amount), interval, nextOn }),
    onSuccess: onDone,
  });
  const ok = description.trim() && cents(amount) && nextOn;
  return (
    <div className="mb-4 space-y-3 rounded-lg border border-border p-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[9rem_minmax(0,1fr)_8rem_8rem_10rem]">
        <select aria-label="Service" className={select} value={module} onChange={(e) => setModule(e.target.value as Module)}>
          {MODULES.map((m) => (
            <option key={m} value={m}>
              {MODULE_LABELS[m]}
            </option>
          ))}
        </select>
        <Input aria-label="Description" placeholder="Monthly bookkeeping" value={description} onChange={(e) => setDescription(e.target.value)} />
        <Input aria-label="Amount" inputMode="decimal" className="tabular text-right" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <select aria-label="Every" className={select} value={interval} onChange={(e) => setInterval(e.target.value as ScheduleInterval)}>
          {(["month", "quarter", "year"] as const).map((i) => (
            <option key={i} value={i}>
              {INTERVAL_LABELS[i].replace(/^a /, "Every ")}
            </option>
          ))}
        </select>
        <Input aria-label="First invoice on" type="date" value={nextOn} onChange={(e) => setNextOn(e.target.value)} />
      </div>
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" disabled={!ok || save.isPending} onClick={() => save.mutate()}>
          Add recurring fee
        </Button>
      </div>
    </div>
  );
}

// --- Offboarding ------------------------------------------------------------------------------

type OffboardPreview = {
  services: Array<{ module: Module; openWork: number; schedules: number }>;
  openBalanceCents: number;
  pendingProposals: number;
  portalUsers: number;
  history: Array<{ id: string; effectiveOn: string; modules: Module[]; reason: string; createdAt: string }>;
  today: string;
};

export function OffboardButton({ clientId, clientName }: { clientId: string; clientName: string }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { data } = useQuery<OffboardPreview>({ queryKey: [`/api/clients/${clientId}/offboarding`], enabled: open });
  const [modules, setModules] = useState<Module[] | null>(null);
  const [effectiveOn, setEffectiveOn] = useState("");
  const [reason, setReason] = useState<(typeof OFFBOARDING_REASONS)[number]>("client_request");
  const [note, setNote] = useState("");
  const [cancelOpenWork, setCancelOpenWork] = useState(true);
  const [portalAccess, setPortalAccess] = useState<"keep" | "documents_only" | "removed">("documents_only");
  const [sendLetter, setSendLetter] = useState(true);
  const chosen = modules ?? data?.services.map((s) => s.module) ?? [];
  const all = !!data && chosen.length === data.services.length;
  const go = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", `/api/clients/${clientId}/offboard`, { effectiveOn: effectiveOn || data!.today, reason, note, modules: chosen, cancelOpenWork, portalAccess, sendLetter }),
    onSuccess: () => {
      setOpen(false);
      toast({ title: all ? "Client offboarded" : "Services ended", description: sendLetter ? "The disengagement letter is in the client's Engagement Letters folder." : undefined });
    },
  });
  const work = data?.services.filter((s) => chosen.includes(s.module)).reduce((a, s) => a + s.openWork, 0) ?? 0;
  const fees = data?.services.filter((s) => chosen.includes(s.module)).reduce((a, s) => a + s.schedules, 0) ?? 0;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          End services
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>End services for {clientName}</DialogTitle>
        </DialogHeader>
        {!data ? (
          <Loading />
        ) : !data.services.length ? (
          <Empty>This client has no active services.</Empty>
        ) : (
          <div className="space-y-4 text-sm">
            <fieldset className="space-y-1.5">
              <legend className="mb-1 font-medium">Services that end</legend>
              {data.services.map((s) => (
                <label key={s.module} className="flex items-center gap-2">
                  <input type="checkbox" checked={chosen.includes(s.module)} onChange={(e) => setModules(e.target.checked ? [...chosen, s.module] : chosen.filter((m) => m !== s.module))} />
                  {MODULE_LABELS[s.module]}
                  <span className="text-xs text-muted-foreground">
                    {plural(s.openWork, "open engagement")}, {plural(s.schedules, "recurring fee")}
                  </span>
                </label>
              ))}
            </fieldset>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="off-date">Last day of service</Label>
                <Input id="off-date" type="date" max={data.today} value={effectiveOn || data.today} onChange={(e) => setEffectiveOn(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="off-reason">Reason (internal)</Label>
                <select id="off-reason" className={select} value={reason} onChange={(e) => setReason(e.target.value as typeof reason)}>
                  {OFFBOARDING_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {OFFBOARDING_REASON_LABELS[r]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-0.5" checked={cancelOpenWork} onChange={(e) => setCancelOpenWork(e.target.checked)} />
              <span>
                Cancel the open work in these services ({plural(work, "engagement")}). Leave unticked to finish it first.
              </span>
            </label>
            <div className="space-y-1">
              <Label htmlFor="off-portal">Portal access</Label>
              <select id="off-portal" className={select} value={portalAccess} onChange={(e) => setPortalAccess(e.target.value as typeof portalAccess)}>
                <option value="documents_only">Documents only (they can still download their files)</option>
                <option value="keep">Keep as it is</option>
                <option value="removed">Remove</option>
              </select>
              {portalAccess !== "keep" && data.openBalanceCents > 0 && <p className="text-xs text-amber-700 dark:text-amber-300">With {portalAccess === "removed" ? "no access" : "documents only"} the client can't pay the {formatCents(data.openBalanceCents)} still due online.</p>}
            </div>
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-0.5" checked={sendLetter} onChange={(e) => setSendLetter(e.target.checked)} />
              <span>File a disengagement letter in the client's Engagement Letters folder{portalAccess === "removed" ? " (kept internal: send it to the client yourself)" : " and email the client that it is there"}.</span>
            </label>
            <div className="space-y-1">
              <Label htmlFor="off-note">Note (internal)</Label>
              <Textarea id="off-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              {plural(fees, "recurring fee")} will stop.{" "}
              {all ? `${data.pendingProposals ? `${plural(data.pendingProposals, "proposal")} still waiting will be withdrawn. ` : ""}Automatic payment stops and saved payment methods are removed. The client becomes inactive.` : "The client's other services continue."} Invoices already issued are not changed.
            </p>
            {go.error && <p className="text-sm text-destructive">{errorMessage(go.error)}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={!chosen.length || go.isPending} onClick={() => go.mutate()}>
                {all ? "Offboard client" : `End ${plural(chosen.length, "service")}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Firm Settings --------------------------------------------------------------------------

function useLifecycleSettings() {
  const { data: raw } = useQuery<Record<string, unknown>>({ queryKey: ["/api/firm/settings"] });
  const cfg = lifecycleSettings(raw?.clientLifecycle);
  const save = useMutation({ meta: { inlineError: true }, mutationFn: (next: LifecycleSettings) => api.send("PATCH", "/api/firm/settings", { clientLifecycle: next }) });
  return { loaded: !!raw, cfg, save };
}

export function ProposalSettings({ canEdit }: { canEdit: boolean }) {
  const { loaded, cfg, save } = useLifecycleSettings();
  const { toast } = useToast();
  const [v, setV] = useState<LifecycleSettings | null>(null);
  useEffect(() => {
    if (loaded && !v) setV(cfg);
  }, [loaded, cfg, v]);
  // The two sections and the Save button are about this tall (53 rem wide screens, 68.5 rem on a phone); reserving it keeps "Refunds, credits and adjustments" below from jumping down when the settings arrive.
  if (!v) return <LoadingBox label="Loading proposal settings…" className="min-h-[68.5rem] sm:min-h-[60rem] lg:min-h-[53rem]" />;
  const num = (k: "validDays" | "dueDays" | "renewalLeadDays" | "autopayRetryDays", label: string, hint: string) => (
    <div className="space-y-1">
      <Label htmlFor={`ls-${k}`}>{label}</Label>
      <Input id={`ls-${k}`} type="number" className="tabular" disabled={!canEdit} value={v[k]} onChange={(e) => setV({ ...v, [k]: Number(e.target.value) })} />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
  return (
    <div className="space-y-6">
      <Section title="Proposals and recurring billing" description="There is no price list: each proposal and renewal is priced for the client.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {num("validDays", "Days to accept a proposal", "After this, a sent proposal expires.")}
          {num("dueDays", "Days to pay a recurring invoice", "Automatic payment is charged on the issue date.")}
          {num("renewalLeadDays", "Days before a term ends to draft its renewal", "14 to 120 days.")}
          {num("autopayRetryDays", "Days before retrying a declined payment", "Tried once more, then left to be paid another way.")}
        </div>
      </Section>
      <Section
        title="Engagement letter terms"
        description="Used for new proposals and renewals; each proposal can still be edited. {{firm}} and {{client}} are filled in."
        action={
          canEdit && (
            <Button variant="ghost" size="sm" onClick={() => setV({ ...v, terms: DEFAULT_LIFECYCLE_SETTINGS.terms })}>
              Restore the standard terms
            </Button>
          )
        }
      >
        <Textarea aria-label="Engagement letter terms" rows={20} className="font-mono text-xs" disabled={!canEdit} value={v.terms} onChange={(e) => setV({ ...v, terms: e.target.value })} />
        <p className="mt-2 text-xs text-muted-foreground">Have the firm's own counsel or insurer review these terms before relying on them.</p>
      </Section>
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
      {canEdit && (
        <div className="flex justify-end">
          <Button disabled={save.isPending} onClick={() => save.mutate(v, { onSuccess: () => toast({ title: "Settings saved" }) })}>
            Save settings
          </Button>
        </div>
      )}
    </div>
  );
}

type ReviewList = { items: Array<{ id: string; clientId: string; clientName: string; status: string; sendAfter: string; sentAt: string | null; clickedAt: string | null; skipReason: string | null }>; sent: number; opened: number; scheduled: number };

export function ReviewSettings({ canEdit, me }: { canEdit: boolean; me: Me }) {
  const { loaded, cfg, save } = useLifecycleSettings();
  const { toast } = useToast();
  const [v, setV] = useState<LifecycleSettings["reviews"] | null>(null);
  const { data } = useQuery<ReviewList>({ queryKey: ["/api/review-requests"] });
  const skip = useMutation({ mutationFn: (id: string) => api.send("POST", `/api/review-requests/${id}/skip`) });
  useEffect(() => {
    if (loaded && !v) setV(cfg.reviews);
  }, [loaded, cfg.reviews, v]);
  const rows = useMemo(() => data?.items ?? [], [data]);
  if (!v) return <Loading />;
  const urlOk = !v.url || /^https:\/\/\S+$/.test(v.url);
  return (
    <div className="space-y-6">
      <StatRow
        stats={[
          { label: "Requests sent this year", value: data?.sent ?? 0 },
          { label: "Opened the review page", value: data?.opened ?? 0, tone: "success" },
          { label: "Scheduled", value: data?.scheduled ?? 0 },
          { label: "Status", value: v.enabled && v.url ? "On" : "Off" },
        ]}
      />
      <Section title="Review requests" description="A short email asking for a review, a few days after a client's work is completed, at most once a year per client. Every client is asked the same way; requests are never sent only to clients who seem satisfied.">
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" disabled={!canEdit} checked={v.enabled} onChange={(e) => setV({ ...v, enabled: e.target.checked })} />
            Send review requests
          </label>
          <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
            <div className="min-w-0 space-y-1">
              <Label htmlFor="rv-url">The firm's review page</Label>
              <Input id="rv-url" disabled={!canEdit} placeholder="https://g.page/r/…/review" value={v.url ?? ""} onChange={(e) => setV({ ...v, url: e.target.value.trim() || null })} className={cn(!urlOk && "border-destructive")} />
              <p className="text-xs text-muted-foreground">In Google Business Profile: Ask for reviews › copy the link. It must start with https://.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rv-delay">Days to wait</Label>
              <Input id="rv-delay" type="number" min={0} max={30} className="tabular" disabled={!canEdit} value={v.delayDays} onChange={(e) => setV({ ...v, delayDays: Number(e.target.value) })} />
              <p className="text-xs text-muted-foreground">After the work is completed.</p>
            </div>
          </div>
          {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
          {canEdit && (
            <div className="flex justify-end">
              <Button disabled={!urlOk || (v.enabled && !v.url) || save.isPending} onClick={() => save.mutate({ ...cfg, reviews: v }, { onSuccess: () => toast({ title: "Settings saved" }) })}>
                Save settings
              </Button>
            </div>
          )}
        </div>
      </Section>
      <Section title="Recent requests" description="Clients can stop these requests from the email; staff can stop them on the client's Billing tab.">
        {!rows.length ? (
          <Empty>No review requests yet. Once turned on, they are scheduled as work is completed.</Empty>
        ) : (
          <TableScroll label="Recent review requests table">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Sent</th>
                  <th className="px-3 py-2 font-medium">Opened</th>
                  <th className="py-2 pl-3 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.slice(0, 50).map((r) => (
                  <tr key={r.id}>
                    <td className="max-w-[14rem] truncate py-2 pr-3">{r.clientName}</td>
                    <td className="tabular whitespace-nowrap px-3 py-2">{r.sentAt ? formatDate(r.sentAt) : r.status === "scheduled" ? `Due ${formatDate(r.sendAfter)}` : "—"}</td>
                    <td className="tabular whitespace-nowrap px-3 py-2">{r.clickedAt ? formatDate(r.clickedAt) : "—"}</td>
                    <td className="py-2 pl-3 text-right">
                      {r.status === "scheduled" && hasPermission(me, "clients.write") ? (
                        <Button variant="ghost" size="sm" onClick={() => skip.mutate(r.id)}>
                          Skip
                        </Button>
                      ) : (
                        <span className="text-xs capitalize text-muted-foreground">{r.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Section>
    </div>
  );
}

/** The client's review requests switch (on the client's Billing tab). */
export function ReviewOptOut({ clientId, optOut }: { clientId: string; optOut: boolean }) {
  const set = useMutation({ mutationFn: (v: boolean) => api.send("POST", `/api/clients/${clientId}/review-requests`, { optOut: v }) });
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={!optOut} disabled={set.isPending} onChange={(e) => set.mutate(!e.target.checked)} />
      Send this client review requests
    </label>
  );
}
